import { useState, useEffect, useCallback } from 'react';
import { heroSlides } from '@/mocks/homeData';
import SearchBar from '@/pages/shop/components/SearchBar';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [heroSearchQuery, setHeroSearchQuery] = useState('');

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning]
  );

  const nextSlide = useCallback(() => {
    goToSlide((current + 1) % heroSlides.length);
  }, [current, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((current - 1 + heroSlides.length) % heroSlides.length);
  }, [current, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[460px] md:h-[580px] lg:h-[660px] overflow-x-hidden mt-12 md:mt-14">
      {/* Background images */}
      {heroSlides.map((s, idx) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            idx === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover object-top"
            loading={idx === 0 ? 'eager' : 'lazy'}
          />
          <div className="absolute inset-0 bg-foreground-950/50" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 flex items-start pt-6 md:pt-10 lg:pt-14 h-full w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-xl">
          {slide.badge && (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 ${
                slide.badgeColor === 'accent'
                  ? 'bg-accent-500 text-foreground-950'
                  : 'bg-primary-500 text-background-50'
              }`}
            >
              {slide.badge}
            </span>
          )}
          <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-background-50 leading-tight mb-4 text-balance">
            {slide.title}
          </h1>
          <p className="text-base md:text-lg text-background-100/90 mb-6 md:mb-8 leading-relaxed max-w-md text-balance">
            {slide.subtitle}
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={slide.link}
              className="inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm md:text-base hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/25 whitespace-nowrap"
            >
              {slide.cta}
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-3.5 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 font-semibold text-sm md:text-base hover:bg-background-50/25 transition-colors whitespace-nowrap"
            >
              Browse All
            </a>
          </div>

          {/* Search bar */}
          <div className="mt-8 w-full">
            <SearchBar
              searchQuery={heroSearchQuery}
              onSearchChange={setHeroSearchQuery}
              placeholder="Search products, brands, categories..."
            />
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        type="button"
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 flex items-center justify-center text-background-50 hover:bg-background-50/40 transition-colors"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 flex items-center justify-center text-background-50 hover:bg-background-50/40 transition-colors"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-14 md:bottom-20 lg:bottom-24 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => goToSlide(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === current
                ? 'w-8 bg-primary-500'
                : 'w-2 bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}